import api from '@/global/api';
import _toLower from 'lodash/toLower';
import qs from 'query-string';
import _get from 'lodash/get';
import _toUpper from 'lodash/toUpper';
import { convertNegativeToZero } from '@/shared/utils/convertNegativeToZero';
import { NETWORK_COINS } from '@/processes/Router';
import _sortBy from 'lodash/sortBy';
import { getApiMiningType } from '@/shared/utils/apiMiningType';

export const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBillingAcc: build.query({
      query: (params = {}) => {
        const {
          miner,
          coin,
        } = params;

        return {
          url: `/v1/stats/miner/${miner}/${coin}/billing-acc`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const {
          totalPaid: total,
          totalUnpaid: unpaid,
          expectedReward24H,
          expectedRewardWeek,
          succeedPayouts: lastPayout,
          earningStats,
          walletStatus,
        } = response;
        return {
          total: Number(total) || 0,
          unpaid: Number(unpaid) || 0,
          expectedReward24H: Number(expectedReward24H) || 0,
          expectedRewardWeek: Number(expectedRewardWeek) || 0,
          lastPayout: _get(lastPayout, [0], {}),
          earningStats: _sortBy(earningStats || [], 'timestamp'),
          walletStatus,
        };
      }
    }),
    getRewardsStats: build.query({
      query: (params = {}) => {
        const {
          coin,
          account,
          miner,
        } = params;

        const coinToLower = _toLower(coin);
        const { urlPart } = getApiMiningType({ miner, account, coin: coinToLower });
        const queryParams = qs.stringify({ currency: coin }, { skipNull: true });

        const url = `/v1/${urlPart}/rewards-stats${queryParams ? `?${queryParams}` : ''}`;

        return {
          url,
          method: 'GET',
        }

      },
      transformResponse: (response: any) => {
        const { available = 0, locked = 0, total = 0, estimated = 0 } = response;
        const unpaid = convertNegativeToZero(available) + convertNegativeToZero(locked);
        const altcoinUnpaid = convertNegativeToZero(available) + convertNegativeToZero(estimated) + convertNegativeToZero(locked);
        return {
          total: convertNegativeToZero(total) - convertNegativeToZero(available),
          unpaid,
          altcoinUnpaid,
          available: convertNegativeToZero(available),
          locked: convertNegativeToZero(locked),
          estimated: convertNegativeToZero(estimated),
        };
      }
    }),
    getWalletAddress: build.query({
      keepUnusedDataFor: 0,
      query: (params = {}) => {
        const {
          coin,
          account,
        } = params;

        const upperCoin = _toUpper(coin);
        const network = _get(NETWORK_COINS, upperCoin);

        const queryParams = qs.stringify({
          network,
          currency: upperCoin,
        }, { skipNull: true })

        return {
          url: `/v1/accounts/${account}/payout-address?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const {
          address,
          updatedAt,
          memo,
          tag,
          valid,
          unlockAt,
        } = response?.address || {};
        return {
          address,
          updatedAt,
          memo,
          unlockAt,
          tag,
          valid,
        };
      }
    }),
    getDailyEarnings: build.query({
      query: (params = {}) => {
        const {
          coin,
          account,
          miner,
        } = params;

        const { urlPartShort } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPartShort}/${_toLower(coin)}/estimated-daily-earnings`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const { amount } = response || {};

        return {
          amount
        };
      }
    }),
    getEarningsStats: build.query({
      query: (params = {}) => {
        const {
          coin,
          account,
          miner,
        } = params;

        const { urlPart } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPart}/earnings-stats`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const { earnings } = response || [];

        return earnings;
      }
    }),
  }),
});

export const {
  useGetBillingAccQuery,
  useGetRewardsStatsQuery,
  useGetWalletAddressQuery,
  useGetDailyEarningsQuery,
  useGetEarningsStatsQuery,
} = extendedApi;

export const {
  getBillingAcc,
  getRewardsStats,
  getWalletAddress,
  getDailyEarnings,
  getEarningsStats,
} = extendedApi.endpoints;
